import styles from 'components/search/search-columns/source-column/source-column-filter.module.css'

import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import { RxPlus } from '@react-icons/all-files/rx/RxPlus'
import React, { useCallback, useMemo, useState } from 'react'

import {
  selectSearchPage,
  setSourceScope,
} from '@disco/state-management/reducers/search-page'
import { hasActiveFilters, resolveSourceScope } from '@disco/utils/search-page'

import type { SourceScope } from 'types/search-page'

import { Checkbox, DropdownMenu, Flex, Text, Tooltip } from 'components/ds'
import type { CatalogSelection } from 'components/search/search-columns/source-column/catalog-picker-modal'
import { CatalogPickerModal } from 'components/search/search-columns/source-column/catalog-picker-modal'
import { MOCK_CATALOGS } from 'components/search/search-columns/source-column/catalog-picker-modal/mock-catalogs'
import { useCatalogSelection } from 'components/search/search-columns/source-column/catalog-selection-store'

import { useDispatch, useSelector } from 'hooks/use-redux'

const MDS_SUPPORT_URL =
  'https://support.disco.ac/home/multidisco-search-feature-guide-for-music-supervisors'

const SOURCE_OPTIONS: {
  label: string
  value: SourceScope
  tooltip?: string
}[] = [
  { label: 'My DISCO', value: 'business' },
  {
    label: 'Search across DISCO',
    value: 'mds',
    tooltip: 'What is this?',
  },
]

export type SourceFilterSummary = {
  /** The user has narrowed the sources beyond the browse default. */
  isFiltered: boolean
  /** Checked sources: each full scope, picked catalog and picked group. */
  count: number
}

type SourceFilterMenuProps = {
  /** Rendered as the menu trigger, so it must forward refs and props. */
  renderTrigger: (summary: SourceFilterSummary) => React.ReactElement
}

/**
 * The Source filter menu (scopes, picked catalogs, groups) and its catalog
 * picker, shared by the Source column header and the search page filter bar.
 */
export const SourceFilterMenu = ({ renderTrigger }: SourceFilterMenuProps) => {
  const dispatch = useDispatch()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const {
    selection: catalogSelection,
    setSelection: setCatalogSelection,
    groups,
    setGroups,
  } = useCatalogSelection()
  const { sourceScope, hasSubmittedSearch, filters } =
    useSelector(selectSearchPage)

  // Flag (not live query) so checkboxes don't flip per keystroke; filters stay
  // live since filter changes refetch atomically.
  const hasSearched = useMemo(
    () => hasSubmittedSearch || hasActiveFilters(filters),
    [hasSubmittedSearch, filters],
  )

  // `sourceScope` null = untouched popover; resolve so checkboxes mirror the
  // searched scope (business-only browsing, both once searched).
  const displayedScopes = useMemo<SourceScope[]>(
    () => resolveSourceScope(sourceScope, hasSearched),
    [sourceScope, hasSearched],
  )

  const hasCatalogSelection =
    catalogSelection.catalogIds.length + catalogSelection.groupIds.length > 0

  const handleToggle = useCallback(
    (value: SourceScope) => {
      const newScopes = displayedScopes.includes(value)
        ? displayedScopes.filter(s => s !== value)
        : [...displayedScopes, value]

      // Picked catalogs are a subset of MDS, so unchecking the last scope
      // still leaves those catalogs to search.
      if (newScopes.length === 0 && hasCatalogSelection) newScopes.push('mds')

      // Defensive fallback: the dropdown disables the last checked item so
      // this branch is unreachable from the UI, but guards against future
      // changes (keyboard shortcuts, programmatic toggles, etc.).
      const [firstScope, ...restScopes] = newScopes
      if (!firstScope) return

      // The search page's change-detection effect fetches on sourceScope change.
      dispatch(setSourceScope([firstScope, ...restScopes]))
    },
    [dispatch, displayedScopes, hasCatalogSelection],
  )

  // Clicking a partial MDS selection clears it entirely: the picked catalogs
  // and MDS itself.
  const handleDeselectMds = () => {
    setCatalogSelection({ catalogIds: [], groupIds: [] })
    if (displayedScopes.includes('mds')) {
      dispatch(setSourceScope(['business']))
    }
  }

  // Highlight only when the user has explicitly narrowed the scope, not for the
  // business-only browse default (`sourceScope === null`).
  const isFiltered =
    Boolean(sourceScope && sourceScope.length < SOURCE_OPTIONS.length) ||
    hasCatalogSelection

  const pickedCatalogs = MOCK_CATALOGS.filter(c =>
    catalogSelection.catalogIds.includes(c.id),
  )

  // Every group is listed for one-click selection, pinned ones first.
  const menuGroups = [
    ...groups.filter(g => g.isPinned),
    ...groups.filter(g => !g.isPinned),
  ]

  const toggleInSelection = (kind: keyof CatalogSelection, id: string) =>
    setCatalogSelection({
      ...catalogSelection,
      [kind]: catalogSelection[kind].includes(id)
        ? catalogSelection[kind].filter(i => i !== id)
        : [...catalogSelection[kind], id],
    })

  // A partial MDS counts through its picked catalogs and groups instead.
  const count =
    displayedScopes.filter(scope => !(scope === 'mds' && hasCatalogSelection))
      .length +
    catalogSelection.catalogIds.length +
    catalogSelection.groupIds.length

  return (
    <>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger>
          {renderTrigger({ isFiltered, count })}
        </DropdownMenu.Trigger>
        <DropdownMenu.Content variant="soft">
          {SOURCE_OPTIONS.map(option => {
            const isChecked = displayedScopes.includes(option.value)
            const isPartialMds = option.value === 'mds' && hasCatalogSelection
            // A partial MDS is the last selection when My DISCO is unchecked.
            const isLastChecked = isPartialMds
              ? !displayedScopes.includes('business')
              : isChecked &&
                displayedScopes.length === 1 &&
                !hasCatalogSelection

            return (
              <React.Fragment key={option.value}>
                <DropdownMenu.Item
                  disabled={isLastChecked}
                  onSelect={e => {
                    e.preventDefault()
                    if (isPartialMds) handleDeselectMds()
                    else handleToggle(option.value)
                  }}
                  asChild
                >
                  <Text size="2" as="div" className={styles.optionLabel}>
                    <Flex align="center" gap="2">
                      <Checkbox
                        checked={isPartialMds ? 'indeterminate' : isChecked}
                        disabled={isLastChecked}
                      />
                      {option.label}
                      {option.tooltip && (
                        <Tooltip content={option.tooltip} avoidCollisions>
                          <a
                            href={MDS_SUPPORT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Info about results from across DISCO"
                            onClick={e => e.stopPropagation()}
                            className={styles.infoLink}
                          >
                            <RxInfoCircled />
                          </a>
                        </Tooltip>
                      )}
                    </Flex>
                  </Text>
                </DropdownMenu.Item>
                {/* Picked catalogs are a subset of MDS, so they nest under it. */}
                {option.value === 'mds' &&
                  pickedCatalogs.map(catalog => (
                    <DropdownMenu.Item
                      key={catalog.id}
                      onSelect={e => {
                        e.preventDefault()
                        toggleInSelection('catalogIds', catalog.id)
                      }}
                      asChild
                    >
                      <Text
                        size="2"
                        as="div"
                        className={`${styles.optionLabel} ${styles.childOption}`}
                      >
                        <Flex align="center" gap="2">
                          <Checkbox checked />
                          <span className={styles.itemName} translate="no">
                            {catalog.name}
                          </span>
                        </Flex>
                      </Text>
                    </DropdownMenu.Item>
                  ))}
              </React.Fragment>
            )
          })}
          <DropdownMenu.Separator />
          {menuGroups.length > 0 && (
            <DropdownMenu.Label>Groups</DropdownMenu.Label>
          )}
          {menuGroups.map(group => (
            <DropdownMenu.Item
              key={group.id}
              onSelect={e => {
                e.preventDefault()
                toggleInSelection('groupIds', group.id)
              }}
              asChild
            >
              <Text size="2" as="div" className={styles.optionLabel}>
                <Flex align="center" gap="2">
                  <Checkbox
                    checked={catalogSelection.groupIds.includes(group.id)}
                  />
                  <span className={styles.itemName}>{group.name}</span>
                  <Text size="1" color="gray">
                    {group.catalogIds.length}
                  </Text>
                </Flex>
              </Text>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Item onSelect={() => setIsPickerOpen(true)}>
            <Flex align="center" gap="2">
              <RxPlus />
              <span>
                {hasCatalogSelection
                  ? 'Edit catalogs & groups…'
                  : 'Choose catalogs & groups…'}
              </span>
            </Flex>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <CatalogPickerModal
        open={isPickerOpen}
        onOpenChange={setIsPickerOpen}
        catalogs={MOCK_CATALOGS}
        groups={groups}
        onGroupsChange={setGroups}
        selection={catalogSelection}
        onApply={setCatalogSelection}
      />
    </>
  )
}
